const STATUS_META = {
  present: { label: "Present", shortLabel: "P", tone: "success" },
  absent: { label: "Absent", shortLabel: "A", tone: "danger" },
  late: { label: "Late", shortLabel: "L", tone: "warning" },
  weekly_off: { label: "Weekly Off", shortLabel: "WO", tone: "muted" },
  on_leave: { label: "On Leave", shortLabel: "LV", tone: "info" },
  holiday: { label: "Holiday", shortLabel: "H", tone: "violet" },
  "": { label: "", shortLabel: "", tone: "default" },
};

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function getCurrentMonthKey() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonthKey(monthKey, step) {
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const date = new Date(year, month - 1 + Number(step || 0), 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonthHeading(monthKey) {
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const date = new Date(year, month - 1, 1);

  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatAttendanceStatus(status) {
  return STATUS_META[status]?.label || "—";
}

export function getAttendanceStatusMeta(status) {
  return STATUS_META[status] || STATUS_META[""];
}

export function formatAttendanceTime(value) {
  return value || "—";
}

export function formatAttendanceHours(value) {
  return value || "—";
}

export function buildEmployeeAttendanceCalendar(monthKey, entries) {
  const entryMap = new Map((entries || []).map((entry) => [entry.dateKey, entry]));
  const [year, month] = String(monthKey || getCurrentMonthKey())
    .split("-")
    .map((value) => Number(value));

  const firstDate = new Date(year, month - 1, 1);
  const lastDate = new Date(year, month, 0);
  const leadingDays = firstDate.getDay();
  const totalDays = lastDate.getDate();

  const cells = [];

  for (let index = 0; index < leadingDays; index += 1) {
    cells.push({
      key: `empty-start-${index}`,
      isPlaceholder: true,
    });
  }

  for (let day = 1; day <= totalDays; day += 1) {
    const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const entry = entryMap.get(dateKey);

    cells.push({
      key: dateKey,
      dateKey,
      day,
      isPlaceholder: false,
      status: entry?.status || "",
      checkInTime: entry?.checkInTime || "",
      checkOutTime: entry?.checkOutTime || "",
      meta: getAttendanceStatusMeta(entry?.status || ""),
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({
      key: `empty-end-${cells.length}`,
      isPlaceholder: true,
    });
  }

  return {
    weekdays: WEEKDAY_LABELS,
    cells,
  };
}

export function createAttendanceDonutStyle(summary) {
  const present = Number(summary?.present || 0);
  const absent = Number(summary?.absent || 0);
  const late = Number(summary?.late || 0);
  const weeklyOff = Number(summary?.weeklyOff || 0);
  const onLeave = Number(summary?.onLeave || 0);
  const holiday = Number(summary?.holiday || 0);
  const total = Math.max(
    1,
    present + absent + late + weeklyOff + onLeave + holiday
  );

  const percentages = {
    present: (present / total) * 100,
    absent: (absent / total) * 100,
    late: (late / total) * 100,
    weeklyOff: (weeklyOff / total) * 100,
    onLeave: (onLeave / total) * 100,
    holiday: (holiday / total) * 100,
  };

  const stops = [];
  let cursor = 0;

  [
    ["#6acb7c", percentages.present],
    ["#ff6d6d", percentages.absent],
    ["#ffbf47", percentages.late],
    ["#b8beca", percentages.weeklyOff],
    ["#4d8cf7", percentages.onLeave],
    ["#9a6bff", percentages.holiday],
  ].forEach(([color, size]) => {
    if (!size) return;
    stops.push(`${color} ${cursor}% ${cursor + size}%`);
    cursor += size;
  });

  return `conic-gradient(${stops.join(", ") || "#edf0f5 0% 100%"})`;
}

export function downloadAttendanceCsv(fileName, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
